import T from 'T'
import {
  isRegenerateBtnEnabled,
  isMarkAsSentBtnEnabled,
  isEditBtnEnabled,
  isDeleteBtnEnabled,
  isRevertToDraftEnabled,
  isRevertCreditMemoToDraftEnabled,
  isDeleteUnpaidEnabled,
  isMoveToAccountCreditEnabled,
  isMakePaymentEnabled,
} from 'data/billing/InvoiceActionSelectors'
import { checkIsInvoiceLocked } from 'components/billing/common/lock-billing-date/utils'
import { INVOICE_STATUS } from 'settings/constants/billing'
import { INVOICE_ACTION_KEYS } from './invoiceActionKeys'

const { UNPAID, PARTIAL, PAID } = INVOICE_STATUS
const {
  EDIT,
  POST,
  POST_AND_SEND,
  REGENERATE,
  REGENERATE_AND_REPOST,
  EMAIL,
  CUSTOM_EMAIL,
  ADD_TO_GROUP,
  REMOVE_FROM_GROUP,
  MAKE_PAYMENT,
  MOVE_TO_ACCOUNT_CREDIT,
  REVERT_TO_DRAFT,
  REVERT_CREDIT_MEMO_TO_DRAFT,
  DELETE,
  DELETE_UNPAID,
} = INVOICE_ACTION_KEYS

/**
 * Determine the invoice state based on status and payment amounts
 * Returns: 'draft', 'fully-unpaid', 'partly-unpaid', or 'paid'
 */
export const getInvoiceState = (invoice = {}) => {
  const { status, amountPaidCents = 0, amountCents = 0 } = invoice

  if (status === PARTIAL) {
    return 'draft'
  }

  if (status === PAID) {
    return 'paid'
  }

  if (status === UNPAID) {
    if (amountPaidCents > 0 && amountPaidCents < amountCents) {
      return 'partly-unpaid'
    }
    return 'fully-unpaid'
  }
  return 'draft'
}

/**
 * Base invoice actions configuration
 * All possible actions with their properties and rules
 */
const BASE_INVOICE_ACTIONS = {
  [EDIT]: {
    key: EDIT,
    label: T.EDIT,
    isDisabled: (invoice, context) => context.isLocked || !isEditBtnEnabled(invoice),
  },
  [POST]: {
    key: POST,
    label: T.POST,
    isDisabled: (invoice, context) => context.isLocked || !isMarkAsSentBtnEnabled(invoice),
  },
  [POST_AND_SEND]: {
    key: POST_AND_SEND,
    label: T.POST_AND_EMAIL,
    isDisabled: (invoice, context) => context.isLocked || !isMarkAsSentBtnEnabled(invoice),
  },
  [REGENERATE]: {
    key: REGENERATE,
    label: T.REGENERATE,
    isDisabled: (invoice, context) => context.isLocked || !isRegenerateBtnEnabled(invoice),
  },
  [REGENERATE_AND_REPOST]: {
    key: REGENERATE_AND_REPOST,
    label: T.REGENERATE_AND_REPOST,
    isDisabled: (invoice, context) => context.isLocked || !isRegenerateBtnEnabled(invoice),
  },
  [EMAIL]: {
    key: EMAIL,
    label: T.EMAIL,
    isVisible: () => true,
    isDisabled: () => false,
  },
  [CUSTOM_EMAIL]: {
    key: CUSTOM_EMAIL,
    label: T.CUSTOM_EMAIL,
    isVisible: () => true,
    isDisabled: () => false,
  },
  [ADD_TO_GROUP]: {
    key: ADD_TO_GROUP,
    label: T.ADD_TO_INVOICE_GROUP,
    isVisible: (invoice, context) => context.showAddToGroupAction && !context.groupId,
    isDisabled: (invoice, context) => context.isLocked || !context.showAddToGroupAction,
  },
  [REMOVE_FROM_GROUP]: {
    key: REMOVE_FROM_GROUP,
    label: T.REMOVE_FROM_GROUP,
    isVisible: (invoice, context) => context.groupId,
    isDisabled: (invoice, context) => context.isLocked || !context.groupId,
  },
  [MAKE_PAYMENT]: {
    key: MAKE_PAYMENT,
    label: T.APPLY_PAYMENT,
    isDisabled: (invoice, context) => context.isLocked || !isMakePaymentEnabled(invoice),
  },
  [MOVE_TO_ACCOUNT_CREDIT]: {
    key: MOVE_TO_ACCOUNT_CREDIT,
    label: T.MOVE_PAYMENT_TO_ACCOUNT_CREDIT,
    isDisabled: (invoice, context) => context.isLocked || !isMoveToAccountCreditEnabled(invoice),
  },
  [REVERT_TO_DRAFT]: {
    key: REVERT_TO_DRAFT,
    label: T.CHANGE_BACK_TO_DRAFT,
    isDisabled: (invoice, context) => context.isLocked || !isRevertToDraftEnabled(invoice),
    style: { color: 'warning.main' },
    requiresConfirmation: true,
  },
  [DELETE]: {
    key: DELETE,
    label: T.DELETE,
    isDisabled: (invoice, context) => context.isLocked || !isDeleteBtnEnabled(invoice),
    style: { color: 'error.main' },
    requiresConfirmation: true,
  },
  [DELETE_UNPAID]: {
    key: DELETE_UNPAID,
    label: T.DELETE,
    isDisabled: (invoice, context) => context.isLocked || !isDeleteUnpaidEnabled(invoice),
    style: { color: 'error.main' },
    requiresConfirmation: true,
  },
  [REVERT_CREDIT_MEMO_TO_DRAFT]: {
    key: REVERT_CREDIT_MEMO_TO_DRAFT,
    label: T.REVERT_CREDIT_MEMO_TO_DRAFT,
    isVisible: invoice => isRevertCreditMemoToDraftEnabled(invoice),
    isDisabled: (invoice, context) => context.isLocked || !isRevertCreditMemoToDraftEnabled(invoice),
    style: { color: 'warning.main' },
    requiresConfirmation: true,
  },
}

/**
 * State-based invoice action configuration
 * Maps each invoice state to its available actions in order
 */
const INVOICE_ACTIONS_BY_STATE = {
  draft: [
    BASE_INVOICE_ACTIONS[EDIT],
    BASE_INVOICE_ACTIONS[REGENERATE],
    BASE_INVOICE_ACTIONS[POST],
    BASE_INVOICE_ACTIONS[POST_AND_SEND],
    BASE_INVOICE_ACTIONS[ADD_TO_GROUP],
    BASE_INVOICE_ACTIONS[REMOVE_FROM_GROUP],
    BASE_INVOICE_ACTIONS[DELETE],
  ],
  'fully-unpaid': [
    BASE_INVOICE_ACTIONS[REGENERATE_AND_REPOST],
    BASE_INVOICE_ACTIONS[EMAIL],
    BASE_INVOICE_ACTIONS[CUSTOM_EMAIL],
    BASE_INVOICE_ACTIONS[ADD_TO_GROUP],
    BASE_INVOICE_ACTIONS[MAKE_PAYMENT],
    BASE_INVOICE_ACTIONS[REMOVE_FROM_GROUP],
    BASE_INVOICE_ACTIONS[REVERT_TO_DRAFT],
    BASE_INVOICE_ACTIONS[DELETE_UNPAID],
  ],
  'partly-unpaid': [
    BASE_INVOICE_ACTIONS[EMAIL],
    BASE_INVOICE_ACTIONS[CUSTOM_EMAIL],
    BASE_INVOICE_ACTIONS[ADD_TO_GROUP],
    BASE_INVOICE_ACTIONS[MAKE_PAYMENT],
    BASE_INVOICE_ACTIONS[MOVE_TO_ACCOUNT_CREDIT],
    BASE_INVOICE_ACTIONS[REMOVE_FROM_GROUP],
  ],
  paid: [
    BASE_INVOICE_ACTIONS[EMAIL],
    BASE_INVOICE_ACTIONS[CUSTOM_EMAIL],
    BASE_INVOICE_ACTIONS[ADD_TO_GROUP],
    BASE_INVOICE_ACTIONS[MOVE_TO_ACCOUNT_CREDIT],
    BASE_INVOICE_ACTIONS[REVERT_TO_DRAFT],
    BASE_INVOICE_ACTIONS[REVERT_CREDIT_MEMO_TO_DRAFT],
    BASE_INVOICE_ACTIONS[REMOVE_FROM_GROUP],
  ],
}

/**
 * Get visible and enabled actions for an invoice based on its state
 * Returns filtered, sorted list of actions available for the invoice
 */
export const getInvoiceActions = (invoice, context = {}) => {
  const invoiceState = getInvoiceState(invoice)
  const actionsForState = INVOICE_ACTIONS_BY_STATE[invoiceState] || []
  const computedContext = {
    ...context,
    isLocked: context.checkIsLocked && invoice.invoiceDate ? checkIsInvoiceLocked(invoice.invoiceDate, context.checkIsLocked) : false,
  }

  return actionsForState
    .filter(action => {
      if (action.isVisible) {
        return action.isVisible(invoice, computedContext)
      }
      return true
    })
    .map(action => ({
      ...action,
      disabled: action.isDisabled(invoice, computedContext),
      isLocked: computedContext.isLocked,
    }))
}
